import { Component, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { debounceTime, filter, mergeMap } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { AppConfig } from '../../app.config';
import { HelperService } from '../../../services/helper-service';
import { ConnectionService } from '../../../services/connection-service';
import { TypeTree } from '../tree-type';

@Component({
    selector: 'tree-edit', templateUrl: './tree-edit.html', styleUrls: ['./tree-edit.scss'],
    standalone: false
})
export class TreeEditComponent implements OnDestroy {
  tree: TypeTree;
  treeObj: any;
  treeClasses: Array<string> = [];
  subscriptions: Array<Subscription>;

  constructor(private appConfig: AppConfig, private router: Router, private route: ActivatedRoute,
    private helper: HelperService, private conn: ConnectionService) {
    this.treeClasses
      .push(...Object.keys(ApiClientConstant.Regimen.Class).map((key: string) => ApiClientConstant.Regimen.Class[key]));
  }

  async getQuestions(name: string): Promise<any> {
    const questions = await this.conn.findQuestions({
      where: {
        $or: ['title', 'uniqueIdentifier'].map((each: string) => ({ [each]: { $regex: name, $options: 'i' } })),
      },
      project: ['uniqueIdentifier', 'title'],
      limit: 10,
    });
    return questions.map((question: any) => ({
      name: this.getDisplayNameOfQuestion(question),
      object: question,
    }));
  }

  ngOnInit(): void {
    this.subscriptions = [];
    this.treeObj = new Table.Tree();
    this.tree = {
      class: '',
      name: '',
      type: ApiClientConstant.Tree.Type.BASIC_INFO,
      questions: [],
      treeTables: [],
      active: true,
    };
    this.subscribeToUrlQueryParams();
  }

  private getAutoComplete(name: string): { controller: UntypedFormControl; options: Observable<Array<{ name: string; object: any }>> } {
    const controller = new UntypedFormControl(name);
    const options = controller.valueChanges.pipe(
      debounceTime(300),
      filter((token: string) => !!token.length),
      mergeMap((token: string) => this.getQuestions(token)));
    return { controller, options };
  }

  subscribeToUrlQueryParams(): void {
    this.subscriptions.push(this.route.parent.params.subscribe(async () => {
      if (this.route.parent.snapshot.data.tree) {
        this.treeObj = this.route.parent.snapshot.data.tree;
        const tree: any = {
          class: '',
          name: '',
          type: ApiClientConstant.Tree.Type.BASIC_INFO,
          questions: [],
        };
        this.helper.convertParseToDictionary(this.treeObj, tree);
        const questions = await this.conn.findQuestions({
          where: { objectId: this.treeObj.get('questions').map((each: any) => each.id) },
          limit: this.treeObj.get('questions').length,
        });
        tree.questions = questions.map((question: any) => ({
          name: this.getDisplayNameOfQuestion(question),
          question,
          autoComplete: this.getAutoComplete(this.getDisplayNameOfQuestion(question)),
        }));
        this.tree = tree;
      }
    }));
  }

  getDisplayNameOfQuestion(question: any): string {
    return `${question.get('uniqueIdentifier')}: ${question.get('title')}`;
  }

  autoCompleteOnSelect(item: { name: string; object: any }, questionItem_: any): void {
    const questionItem = questionItem_;
    questionItem.name = item.name;
    questionItem.question = item.object;
  }

  addQuestion(): void {
    this.tree.questions.push({ autoComplete: this.getAutoComplete('') });
  }

  removeQuestion(index: number): void {
    this.tree.questions.splice(index, 1);
  }

  isTreeValid(): boolean {
    return this.tree.questions.every((i: any) => {
      const item = i;
      if (item.question) return true;
      delete item.name;
      return false;
    });
  }

  saveTree(): void {
    if (!this.isTreeValid()) return;
    this.helper.convertDictionaryToParse(this.tree, this.treeObj);
    this.treeObj.set('questions', this.tree.questions.map((x: any) => x.question));
    this.treeObj.set('nodeMap', []);
    this.treeObj.save()
      .then(() => this.router.navigate([`/tree/${this.treeObj.get('name')}`]))
      .catch((err: any) => alert(err.message.message || err.message));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    delete this.appConfig;
    delete this.router;
    delete this.route;
    delete this.helper;
    delete this.conn;
    delete this.subscriptions;
    delete this.tree;
    delete this.treeObj;
  }

  moveQuestionUp(index: number): void {
    if (index < 1) return;
    const temp = this.tree.questions[index];
    this.tree.questions[index] = this.tree.questions[index - 1];
    this.tree.questions[index - 1] = temp;
  }

  moveQuestionDown(index: number): void {
    if (index < 0 || index >= this.tree.questions.length - 1) return;
    const temp = this.tree.questions[index];
    this.tree.questions[index] = this.tree.questions[index + 1];
    this.tree.questions[index + 1] = temp;
  }
}
